import { MaskedLine, Reveal } from "@/components/Reveal";
import { useLanguage } from "@/lib/i18n";

const Section = ({ title, children }) => (
  <Reveal>
    <section>
      <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">{title}</h2>
      <div className="text-base font-light leading-relaxed text-smoke">{children}</div>
    </section>
  </Reveal>
);

const responsibleBody = (
  <p>
    Wim Jansen Delgado, Gojenbergsweg 62, 21029 Hamburg
    <br />
    +49 170 810 55 68 ·{" "}
    <a href="mailto:mail@wimjansen.de" className="text-bone transition-colors hover:text-ember-deep" data-testid="datenschutz-email-link">
      mail@wimjansen.de
    </a>
  </p>
);

const DE = [
  { title: "1. Verantwortlicher", body: responsibleBody },
  {
    title: "2. Hosting und Server-Logdateien",
    body: <p>Diese Website wird gehostet bei Emergent (Emergent Labs Inc., USA) und über das Auslieferungs-Netzwerk von Cloudflare (Cloudflare, Inc., USA) bereitgestellt. Dabei werden personenbezogene Daten (insbesondere deine IP-Adresse) in die USA übermittelt. Mit Cloudflare besteht ein Vertrag zur Auftragsverarbeitung; die Übermittlung stützt sich dabei auf das EU-US Data Privacy Framework, für das Cloudflare zertifiziert ist. Beim Aufruf dieser Website werden durch die Anbieter automatisch Informationen in sogenannten Server-Logdateien gespeichert (z. B. IP-Adresse, Datum und Uhrzeit, aufgerufene Seite, Browsertyp). Diese Daten sind technisch notwendig, um die Website auszuliefern und ihre Sicherheit zu gewährleisten (Art. 6 Abs. 1 lit. f DSGVO). Eine Zusammenführung mit anderen Datenquellen findet nicht statt.</p>,
  },
  {
    title: "3. Kontaktformular",
    body: <p>Wenn du mir über das Kontaktformular eine Anfrage sendest, werden deine Angaben (Name, E-Mail-Adresse, Projektart, Budget und Nachricht) gespeichert, um die Anfrage zu bearbeiten und Anschlussfragen zu beantworten (Art. 6 Abs. 1 lit. b DSGVO). Für die Zustellung der Anfrage per E-Mail nutze ich den Versanddienst Resend (Resend, Inc., USA); dabei werden die angegebenen Daten zur Zustellung an Resend übermittelt und in den USA verarbeitet. Mit Resend besteht ein Vertrag zur Auftragsverarbeitung; die Übermittlung in die USA erfolgt auf Grundlage von Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO). Darüber hinaus gebe ich deine Daten nicht ohne deine Einwilligung weiter. Du kannst die Löschung deiner Anfrage jederzeit per E-Mail verlangen.</p>,
  },
  {
    title: "4. Cookies und Analyse",
    body: <p>Ein technisch notwendiges Cookie wird nur beim Login in den Admin-Bereich gesetzt. Für Besucher werden keine Cookies und kein lokaler Speicher verwendet. Es werden keine Analyse- oder Tracking-Dienste eingesetzt.</p>,
  },
  {
    title: "5. Schriftarten",
    body: <p>Die Schriftarten dieser Website werden lokal über diese Website ausgeliefert. Es findet keine Verbindung zu Servern von Google oder anderen Drittanbietern statt; deine IP-Adresse wird zu diesem Zweck nicht an Dritte übermittelt.</p>,
  },
  {
    title: "6. Deine Rechte",
    body: <p>Du hast jederzeit das Recht auf Auskunft über deine gespeicherten Daten, auf Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen die Verarbeitung (Art. 15–21 DSGVO). Wende dich dazu einfach an mail@wimjansen.de. Zudem hast du das Recht, dich bei der zuständigen Aufsichtsbehörde zu beschweren, in Hamburg: Der Hamburgische Beauftragte für Datenschutz und Informationsfreiheit.</p>,
  },
  {
    title: "7. SSL-/TLS-Verschlüsselung",
    body: <p>Diese Website nutzt aus Sicherheitsgründen eine SSL-/TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennst du an „https://“ in der Adresszeile deines Browsers.</p>,
  },
];

const EN = [
  { title: "1. Controller", body: responsibleBody },
  {
    title: "2. Hosting and server log files",
    body: <p>This website is hosted by Emergent (Emergent Labs Inc., USA) and delivered via the content delivery network of Cloudflare (Cloudflare, Inc., USA). In this process, personal data (in particular your IP address) is transferred to the USA. A data processing agreement is in place with Cloudflare; the transfer is based on the EU-US Data Privacy Framework, for which Cloudflare is certified. When you visit this website, the providers automatically store information in so-called server log files (e.g. IP address, date and time, page accessed, browser type). This data is technically necessary to deliver the website and ensure its security (Art. 6 (1) lit. f GDPR). It is not merged with other data sources.</p>,
  },
  {
    title: "3. Contact form",
    body: <p>When you send me an enquiry via the contact form, your details (name, email address, project type, budget and message) are stored in order to process the enquiry and answer follow-up questions (Art. 6 (1) lit. b GDPR). To deliver your enquiry by email, I use the delivery service Resend (Resend, Inc., USA); the data you provide is transmitted to Resend for delivery and processed in the USA. A data processing agreement is in place with Resend; the transfer to the USA is based on standard contractual clauses (Art. 46 (2) lit. c GDPR). Beyond this, I do not share your data without your consent. You can request the deletion of your enquiry at any time by email.</p>,
  },
  {
    title: "4. Cookies and analytics",
    body: <p>A technically necessary cookie is only set when logging in to the admin area. No cookies or local storage are used for visitors. No analytics or tracking services are used.</p>,
  },
  {
    title: "5. Fonts",
    body: <p>The fonts on this website are served locally by this website. No connection is made to Google or other third-party servers; your IP address is not transmitted to third parties for this purpose.</p>,
  },
  {
    title: "6. Your rights",
    body: <p>You have the right at any time to access your stored data, to rectification, erasure, restriction of processing, data portability and to object to processing (Art. 15–21 GDPR). Simply contact mail@wimjansen.de. You also have the right to lodge a complaint with the competent supervisory authority, in Hamburg: The Hamburg Commissioner for Data Protection and Freedom of Information.</p>,
  },
  {
    title: "7. SSL/TLS encryption",
    body: <p>For security reasons, this website uses SSL/TLS encryption. You can recognise an encrypted connection by "https://" in your browser's address line.</p>,
  },
];

export default function Datenschutz() {
  const { lang } = useLanguage();
  const sections = lang === "en" ? EN : DE;
  return (
    <div className="mx-auto max-w-7xl px-6 pb-16 pt-28 md:px-12 md:pt-36 lg:px-16" data-testid="datenschutz-page">
      <Reveal>
        <p className="mb-4 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
          {lang === "en" ? "Legal" : "Rechtliches"}
        </p>
      </Reveal>
      <h1 className="font-serif font-light tracking-tight text-bone" data-testid="datenschutz-title">
        <MaskedLine delay={0.1} className="text-[clamp(2.6rem,6vw,5rem)]">
          {lang === "en" ? "Privacy" : "Datenschutz"}
        </MaskedLine>
      </h1>

      <div className="mt-14 grid grid-cols-12 gap-x-6 gap-y-10 md:gap-x-10">
        <div className="col-span-12 space-y-14 md:col-span-7">
          {sections.map((s) => (
            <Section key={s.title} title={s.title}>{s.body}</Section>
          ))}
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
            {lang === "en" ? "Last updated: October 2026" : "Stand: Oktober 2026"}
          </p>
        </div>
      </div>
    </div>
  );
}
